import { Grid, pn, Switch, Tooltip, useElementBreakpoints } from "@compass/core";
import { Select, StudentCustomIdsPaster } from "components";
import { FilterAccordion } from "components/common/CustomFilters/FilterAccordion";
import {
  LkStudentDisplayFilterId,
  LkStudentEnrollmentDisplayFieldId,
  ReportPropertyBase,
  StudentFilter as StudentFilterModel,
  StudentFilterResource,
} from "generated/openApi";
import produce from "immer";
import { useEffect, useMemo, useState } from "react";

export const StudentDemographicsFilterAccordion = (props: {
  resource: StudentFilterResource;
  filter: StudentFilterModel;
  setFilter: (filter: StudentFilterModel) => void;
}) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.properties || [];
  const displayFields = props.resource.enrollmentDisplayFields || [];

  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);
  const [isActiveSelected, setIsActiveSelected] = useState<boolean>(false);
  

  const activeSchoolYear = props.resource.schoolYearsWithActive?.find(x =>
    x.name?.toLowerCase().includes("active")
  );

  const activeSchoolYearId = activeSchoolYear?.id;

  const activeTooltip = activeSchoolYear
    ? `${activeSchoolYear.name.replace(" (Active)", "")}`
    : "No active school year found";

  useEffect(() => {
    if (props.filter && props.resource.schoolYearsWithActive) {
      setIsActiveSelected(hasActiveSelected())
    }
  }, [props.filter, props.resource]);

  const hasActiveSelected = (): boolean => {
    const activeSchoolYear = props.resource.schoolYearsWithActive?.find(x =>x.name?.toLowerCase().includes("active"));

    if (!activeSchoolYear) return false;

    const activeSchoolYearId = activeSchoolYear?.id;

    return props.filter.schoolYears?.includes(activeSchoolYearId) || false;
  }

  const schoolYearField = () => {
    const propertyName = pn<StudentFilterModel>("schoolYears");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.schoolYearsWithActive?.filter((x) => props.filter.schoolYears?.includes(x.id))}
        options={props.resource.schoolYearsWithActive}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYears = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const regionField = () => {
    const propertyName = pn<StudentFilterModel>("regionIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.regions?.filter((x) => props.filter.regionIds?.includes(x.id))}
        options={props.resource.regions}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.regionIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const districtField = () => {
    const propertyName = pn<StudentFilterModel>("districtIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.districts?.filter((x) => props.filter.districtIds?.includes(x.id))}
        options={props.resource.districts}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.districtIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentFilterModel>("schoolIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={schoolDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={props.resource.schools?.filter((x) => props.filter.schoolIds?.includes(x.id))}
        options={props.resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const gradeLevelsField = () => {
    const propertyName = pn<StudentFilterModel>("gradeLevelIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={gradeLevelDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={props.resource.gradeLevels?.filter((x) => props.filter.gradeLevelIds?.includes(x.id))}
        options={props.resource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.gradeLevelIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  // const gradeLevelTermsField = () => {
  //   const propertyName = pn<StudentFilterModel>("gradeLevelTermIds");
  //   const property = properties.find((x) => x.propertyName === propertyName);

  //   return (
  //     <Select
  //       multiple
  //       modelProperty={property}
  //       value={props.resource.gradeLevelTerms?.filter((x) => props.filter.gradeLevelTermIds?.includes(x.id))}
  //       options={props.resource.gradeLevelTerms}
  //       getOptionLabel={(option) => option.name}
  //       onChange={(e, values) =>
  //         props.setFilter(
  //           produce(props.filter, (draft) => {
  //             draft.gradeLevelTermIds = values.map((x) => x.id);
  //           })
  //         )
  //       }
  //     />
  //   );
  // };

  // const endReasonTypesField = () => {
  //   const propertyName = pn<StudentFilterModel>("endReasonTypeIds");
  //   const property = properties.find((x) => x.propertyName === propertyName);

  //   return (
  //     <Select
  //       multiple
  //       modelProperty={property}
  //       value={props.resource.endReasonTypes?.filter((x) => props.filter.endReasonTypeIds?.includes(x.id))}
  //       options={props.resource.endReasonTypes}
  //       getOptionLabel={(option) => option.name}
  //       onChange={(e, values) =>
  //         props.setFilter(
  //           produce(props.filter, (draft) => {
  //             draft.endReasonTypeIds = values.map((x) => x.id);
  //           })
  //         )
  //       }
  //     />
  //   );
  // };

  const studentGroupsField = () => {
    const propertyName = pn<StudentFilterModel>("studentGroupIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.studentGroups?.filter((x) => props.filter.studentGroupIds?.includes(x.id))}
        options={props.resource.studentGroups}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.studentGroupIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const duplicateByField = () => {
    const propertyName = pn<StudentFilterModel>("duplicateByProperties");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.duplicateByProperties?.filter((x) =>
          props.filter.duplicateByProperties?.includes(x.propertyName)
        )}
        options={props.resource.duplicateByProperties}
        getOptionLabel={(option) => option.displayName}
        onChange={(e, values: ReportPropertyBase[]) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.duplicateByProperties = values.map((x) => x.propertyName);
            })
          )
        }
      />
    );
  };

  const programField = () => {
    const propertyName = pn<StudentFilterModel>("programIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.programs?.filter((x) => props.filter.programIds?.includes(x.id))}
        options={props.resource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.programIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  return (

    <Grid container spacing="sm">
      <Grid item xs={fullFieldBreakpoints}>
        <Tooltip title={activeTooltip} arrow>
          <div>
            <Switch
              color="primary"
              checked={isActiveSelected}
              label="Active School Year"
              onChange={(e, checked) =>
                props.setFilter(
                  produce(props.filter, (draft) => {
                    if (!activeSchoolYearId) return;

                    if (checked) {
                      draft.schoolYears = draft.schoolYears || [];

                      if (!draft.schoolYears.includes(activeSchoolYearId)) {
                        draft.schoolYears.push(activeSchoolYearId);
                      }
                    } else {
                      draft.schoolYears =
                        draft.schoolYears?.filter(id => id !== activeSchoolYearId) ?? [];
                    }
                  })
                )
              }
            />
          </div>
        </Tooltip>
      </Grid>

      {props.resource.canAccessRegionFilter &&
        props.resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.Region) && (
          <Grid item xs={fullFieldBreakpoints}>
            {regionField()}
          </Grid>
        )}

      {props.resource.canAccessDistrictFilter &&
        props.resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.District) && (
          <Grid item xs={fullFieldBreakpoints}>
            {districtField()}
          </Grid>
        )}

      {props.resource.canAccessSchoolFilter && schoolDisplayField && (
        <Grid item xs={fullFieldBreakpoints}>
          {schoolField()}
        </Grid>
      )}

      {gradeLevelDisplayField && (
        <Grid item xs={fullFieldBreakpoints}>
          {gradeLevelsField()}
        </Grid>
      )}

      {/* {props.resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.GradeLevelTerm) && (
          <Grid item xs={fullFieldBreakpoints}>
            {gradeLevelTermsField()}
          </Grid>
        )} */}

      {/* <Grid item xs={fullFieldBreakpoints}>
          {endReasonTypesField()}
        </Grid> */}

      <Grid item xs={fullFieldBreakpoints}>
        {studentGroupsField()}
      </Grid>

      <Grid item xs={fullFieldBreakpoints}>
        {duplicateByField()}
      </Grid>

      {props.resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.Program) && (
        <Grid item xs={fullFieldBreakpoints}>
          {programField()}
        </Grid>
      )}

      <Grid item xs={fullFieldBreakpoints}>
        <StudentCustomIdsPaster
          value={props.filter.customIds}
          typeId={props.filter.customIdTypeId}
          onChange={(customIdTypeId, customIds) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.customIdTypeId = customIdTypeId;
                draft.customIds = customIds;
              })
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
